import { useEffect, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { IndexingStatusResponse } from "@/api/admin-api"

import { getIndexingStatus } from "@/api/admin-api"
import { RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"

import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DelegationModeControl } from "./delegation-mode-control"
import { IndexingControlPanel } from "./indexing-management-card"
import { JobHistoryPanel } from "./job-history-panel"
import { useAdminLibrariesQuery } from "../hooks/admin-queries"

export function IndexingManagementSection() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: libraries, isLoading } = useAdminLibrariesQuery()
  const [indexingStatus, setIndexingStatus] = useState<IndexingStatusResponse | null>(null)
  const [isLoadingIndexingStatus, setIsLoadingIndexingStatus] = useState(false)

  const handleCheckStatus = async () => {
    setIsLoadingIndexingStatus(true)
    try {
      const result = await getIndexingStatus()
      setIndexingStatus(result)
    } catch (error) {
      toast.error(t("adminDashboard.indexing.statusError", "Failed to fetch indexing status"), {
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setIsLoadingIndexingStatus(false)
    }
  }

  // Auto-fetch status on mount
  useEffect(() => {
    void handleCheckStatus()
  }, []) // Only run on mount

  return (
    <CollapsibleManagementSection
      icon={<RefreshCw className="size-5" />}
      id="indexing"
      storageKey="admin-management-indexing-expanded"
      title={t("adminDashboard.indexing.title", "Indexing Management")}
      headerActions={
        <div className="flex items-center gap-2">
          <Button
            disabled={isLoadingIndexingStatus || isLoading}
            size="sm"
            type="button"
            variant="outline"
            onClick={handleCheckStatus}
          >
            {isLoadingIndexingStatus ? (
              <Spinner className="mr-2 size-4" />
            ) : (
              <RefreshCw className="mr-2 size-4" />
            )}
            {t("adminDashboard.indexing.checkStatus", "Check Status")}
          </Button>
          <DelegationModeControl capability="indexing" />
        </div>
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="rounded-lg border p-4">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="mt-3 h-8 w-80" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          <IndexingControlPanel
            libraries={libraries || []}
            status={indexingStatus}
            onCheckStatus={handleCheckStatus}
            onRefresh={() => {
              void queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
            }}
          />
          <div className="border-t pt-6">
            <JobHistoryPanel />
          </div>
        </div>
      )}
    </CollapsibleManagementSection>
  )
}
